import { useTranslation } from "react-i18next"

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import { FUNNEL_STAGES } from "@/types/invites"
import type { FunnelData, FunnelStage } from "@/types/invites"

/**
 * OKS-21 — Invite funnel as **shadcn dashboard metric cards**.
 *
 * Replaces the v1 4-step horizontal funnel viz (rejected by CTPO
 * during Phase 3 review for "looking prototype-y"). New shape per
 * UX Spec addendum §2.2 v2:
 *
 *   - 4 `<Card>` stat tiles
 *   - Mobile: 2×2 grid (`grid-cols-2`)
 *   - Desktop: 4-across (`md:grid-cols-4`)
 *   - `CardHeader` = uppercase stage label
 *   - `CardContent` = large numeric (`text-2xl font-semibold tabular-nums`)
 *   - `CardDescription` (sub-line):
 *     - `Generated`: range label ("Last 30 days")
 *     - other 3: "{X}% of {prior_stage}"
 *   - Empty: zero counts render normally; conversion sub-line shows
 *     `—` instead of `0%` (carries the v1 idiom forward)
 *   - **No horizontal arrows between cards** — conversion is encoded
 *     in each card's sub-line.
 *
 * **Server-authoritative** — all counts + conversion ratios come
 * straight from `FunnelData` (BE service `CandidateInvites::FunnelQuery`).
 * FE never recomputes; the funnel always reflects backend's state-
 * derivation rules + brand-TZ bucketing.
 *
 * **A11y** — each card is `role="group"` with an accessible name like
 * "Generated, 24 invites in last 30 days" or "Clicked, 18 invites,
 * 75% of generated". Color is never the sole signal — every value
 * has a label + numeric.
 */

interface InviteFunnelMetricsProps {
  funnel: FunnelData
  /** Range label for the `Generated` card sub-line (e.g.
   *  "Last 30 days" or a custom range). Localised by caller. */
  rangeLabel: string
  className?: string
}

export function InviteFunnelMetrics({
  funnel,
  rangeLabel,
  className,
}: InviteFunnelMetricsProps) {
  const { t } = useTranslation()
  const isEmpty = funnel.totals.generated === 0

  // Display percentages (rounded to nearest int). BE guarantees the
  // ratio is `0` (not NaN) when the prior stage is empty.
  const conversionPct = {
    clicked: Math.round(funnel.conversion.gen_to_click * 100),
    signup_started: Math.round(funnel.conversion.click_to_signup * 100),
    completed: Math.round(funnel.conversion.signup_to_complete * 100),
  } as const

  return (
    <div
      data-testid="invite-funnel-metrics"
      className={cn("grid grid-cols-2 gap-3 md:grid-cols-4", className)}
    >
      {FUNNEL_STAGES.map((stage) => {
        const label = stageLabel(t, stage)
        const count = funnel.totals[stage]
        const subLine = subLineFor(t, stage, {
          rangeLabel,
          isEmpty,
          conversionPct,
        })
        return (
          <MetricCard
            key={stage}
            stage={stage}
            label={label}
            count={count}
            subLine={subLine}
            ariaLabel={ariaLabel(t, stage, {
              label,
              count,
              isEmpty,
              conversionPct,
              rangeLabel,
            })}
          />
        )
      })}
    </div>
  )
}

interface MetricCardProps {
  stage: FunnelStage
  label: string
  count: number
  /** `null` only when the conversion sub-line is hidden (never in
   *  current spec — `Generated` shows the range, others show %). */
  subLine: string | null
  ariaLabel: string
}

function MetricCard({
  stage,
  label,
  count,
  subLine,
  ariaLabel,
}: MetricCardProps) {
  return (
    <Card
      data-testid={`invite-funnel-metric-${stage}`}
      data-stage={stage}
      role="group"
      aria-label={ariaLabel}
      className="gap-2"
    >
      <CardHeader className="pb-0">
        <CardTitle className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p
          data-testid={`invite-funnel-metric-${stage}-count`}
          className="text-foreground text-2xl font-semibold tabular-nums"
        >
          {count}
        </p>
        {subLine !== null && (
          <CardDescription
            data-testid={
              stage === "generated"
                ? undefined
                : `invite-funnel-metric-${stage}-conversion`
            }
            className="mt-1 text-xs"
          >
            {subLine}
          </CardDescription>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Static `t()` calls for stage labels — keeps the inertia_i18n
 * health scanner happy (matches the OKS-17/18 idiom).
 */
function stageLabel(
  t: ReturnType<typeof useTranslation>["t"],
  stage: FunnelStage,
): string {
  switch (stage) {
    case "generated":
      return t("invites.funnel.step.generated")
    case "clicked":
      return t("invites.funnel.step.clicked")
    case "signup_started":
      return t("invites.funnel.step.signup_started")
    case "completed":
      return t("invites.funnel.step.completed")
  }
}

interface SubLineCtx {
  rangeLabel: string
  isEmpty: boolean
  conversionPct: { clicked: number; signup_started: number; completed: number }
}

function subLineFor(
  t: ReturnType<typeof useTranslation>["t"],
  stage: FunnelStage,
  { rangeLabel, isEmpty, conversionPct }: SubLineCtx,
): string {
  if (stage === "generated") {
    return t("invites.funnel.metric.generated.sub_line", {
      range: rangeLabel,
    })
  }
  // Conversion sub-lines: empty range collapses % → "—" so we don't
  // render misleading "0% of 0" values (carries v1's empty-state
  // discipline forward).
  if (isEmpty) {
    return t("invites.funnel.metric.conversion.empty")
  }
  switch (stage) {
    case "clicked":
      return t("invites.funnel.metric.clicked.sub_line", {
        percent: conversionPct.clicked,
      })
    case "signup_started":
      return t("invites.funnel.metric.signup_started.sub_line", {
        percent: conversionPct.signup_started,
      })
    case "completed":
      return t("invites.funnel.metric.completed.sub_line", {
        percent: conversionPct.completed,
      })
  }
}

interface AriaCtx {
  label: string
  count: number
  rangeLabel: string
  isEmpty: boolean
  conversionPct: { clicked: number; signup_started: number; completed: number }
}

function ariaLabel(
  t: ReturnType<typeof useTranslation>["t"],
  stage: FunnelStage,
  { label, count, rangeLabel, isEmpty, conversionPct }: AriaCtx,
): string {
  if (stage === "generated") {
    return t("invites.funnel.metric.generated.aria", {
      label,
      count,
      range: rangeLabel,
    })
  }
  if (isEmpty) {
    return t("invites.funnel.metric.conversion.aria_empty", {
      label,
      count,
    })
  }
  const percent =
    stage === "clicked"
      ? conversionPct.clicked
      : stage === "signup_started"
        ? conversionPct.signup_started
        : conversionPct.completed
  return t("invites.funnel.metric.conversion.aria", {
    label,
    count,
    percent,
  })
}

/**
 * Loading skeleton matching the metric grid shape — used as the
 * Inertia `<Deferred>` fallback so layout doesn't shift when the
 * `funnel` prop hydrates.
 */
export function InviteFunnelMetricsSkeleton({
  className,
}: {
  className?: string
}) {
  return (
    <div
      data-testid="invite-funnel-metrics-loading"
      className={cn("grid grid-cols-2 gap-3 md:grid-cols-4", className)}
      aria-busy="true"
      aria-live="polite"
    >
      {Array.from({ length: 4 }).map((_, idx) => (
        <Card key={idx} className="gap-2">
          <CardHeader className="pb-0">
            <Skeleton className="h-3 w-20" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-7 w-12" />
            <Skeleton className="mt-2 h-3 w-24" />
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
